
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RequestDemoModal } from "./marketing/RequestDemoModal";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle navigation to homepage sections
  const handleSectionNavigation = (sectionId: string) => {
    if (location.pathname === '/') {
      // Already on homepage, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage first, then scroll to section
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm ${
        isScrolled
          ? "py-3"
          : "py-5"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/"
              className="text-2xl font-bold text-saas-navy"
            >
              FlatJive
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/insights/"
              className="text-gray-600 hover:text-saas-blue transition-colors"
            >
              Insights
            </Link>
            <button
              onClick={() => handleSectionNavigation('features')}
              className="text-gray-600 hover:text-saas-blue transition-colors cursor-pointer"
            >
              Features
            </button>
            <button
              onClick={() => handleSectionNavigation('testimonials')}
              className="text-gray-600 hover:text-saas-blue transition-colors cursor-pointer"
            >
              Testimonials
            </button>
            <Link
              to="/pricing/"
              className="text-gray-600 hover:text-saas-blue transition-colors"
            >
              Pricing
            </Link>
            
            <Button 
              asChild 
              variant="outline" 
              className="bg-white border-foreground text-foreground hover:bg-gray-50"
            >
              <a href="https://app.flatjive.com/login" target="_blank" rel="noopener noreferrer">
                Sign In
              </a>
            </Button>
            
            <RequestDemoModal>
              <Button className="bg-saas-blue hover:bg-blue-600 text-white">
                Request a Demo
              </Button>
            </RequestDemoModal>
          </nav>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="ml-4 text-gray-600 hover:text-saas-blue"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 bg-white/95 backdrop-blur-md rounded-lg shadow-lg">
            <div className="flex flex-col space-y-4 p-4">
              <Link
                to="/insights/"
                className="text-gray-600 hover:text-saas-blue transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Insights
              </Link>
              <button
                onClick={() => {
                  handleSectionNavigation('features');
                  setIsMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-saas-blue transition-colors text-left"
              >
                Features
              </button>
              <button
                onClick={() => {
                  handleSectionNavigation('testimonials');
                  setIsMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-saas-blue transition-colors text-left"
              >
                Testimonials
              </button>
              <Link
                to="/pricing/"
                className="text-gray-600 hover:text-saas-blue transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              
              <Button 
                asChild 
                variant="outline" 
                className="bg-white border-foreground text-foreground hover:bg-gray-50 w-full"
              >
                <a 
                  href="https://app.flatjive.com/login" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </a>
              </Button>
              
              <RequestDemoModal>
                <Button className="bg-saas-blue hover:bg-blue-600 text-white w-full">
                  Request a Demo
                </Button>
              </RequestDemoModal>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Navbar;
